import { Router, Route, Switch } from "wouter";
import { TenantProvider, useTenant } from "@/contexts/TenantContext";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ErrorPage } from "@/pages/ErrorPage";
import { LoadingPage } from "@/components/shared/LoadingSpinner";
import { SkipLink } from "@/components/shared/SkipLink";
import { EnvironmentBanner } from "@/components/EnvironmentBanner";
import { TenantNotFoundPage } from "@/pages/NotFoundPage";
import { SearchHomePage } from "@/pages/SearchHomePage";
import { TripSearchResultsPage } from "@/pages/TripSearchResultsPage";
import { SeatSelectionPage } from "@/pages/SeatSelectionPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { PaymentPage } from "@/pages/PaymentPage";
import { ConfirmationPage } from "@/pages/ConfirmationPage";
import { TicketViewPage } from "@/pages/TicketViewPage";
import { TrackDriverPage } from "@/pages/TrackDriverPage";
import { TrackCoordinatorPage } from "@/pages/TrackCoordinatorPage";
import { TrackPassengerPage } from "@/pages/TrackPassengerPage";

/**
 * Tenant-aware content wrapper that applies theme and handles loading/error states
 */
function TenantContent({ children }: { children: React.ReactNode }) {
  const { tenant, isLoading, isNotFound, error } = useTenant();

  // Apply tenant theme colors
  useTenantTheme();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isNotFound) {
    return <TenantNotFoundPage />;
  }

  if (error && !tenant) {
    return (
      <ErrorPage
        type="server"
        message={error}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return <>{children}</>;
}

/**
 * Tenant route wrapper - provides TenantContext for all tenant-specific routes
 */
function TenantRoutes() {
  return (
    <TenantProvider>
      <SkipLink />
      <TenantContent>
        <Switch>
          {/* Seat Selection Page - relative path from /:slug */}
          <Route path="/trips/:tripId/seats">
            <SeatSelectionPage />
          </Route>

          {/* Trip Search Results - relative path from /:slug */}
          <Route path="/trips">
            <TripSearchResultsPage />
          </Route>

          {/* Checkout Page */}
          <Route path="/checkout">
            <CheckoutPage />
          </Route>

          {/* Payment Page */}
          <Route path="/payment">
            <PaymentPage />
          </Route>

          {/* Confirmation Page */}
          <Route path="/confirmation/:ticketNumber">
            <ConfirmationPage />
          </Route>

          {/* Ticket View Page */}
          <Route path="/ticket/:ticketNumber">
            <TicketViewPage />
          </Route>

          {/* Search Home Page - matches /:slug exactly */}
          <Route path="/">
            <SearchHomePage />
          </Route>
        </Switch>
      </TenantContent>
    </TenantProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <EnvironmentBanner />
      <Router>
        <Switch>
          {/* Fleet Tracker public routes — no tenant context required */}
          {/* Passenger view must come BEFORE /driver and /coordinator to avoid shadowing */}
          <Route path="/track/:sessionId">
            <TrackPassengerPage />
          </Route>
          <Route path="/track/:sessionId/driver">
            <TrackDriverPage />
          </Route>
          <Route path="/track/:sessionId/coordinator">
            <TrackCoordinatorPage />
          </Route>

          {/* Root path - show welcome message */}
          <Route path="/">
            <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-2xl font-bold">Welcome to RutaLink</h1>
                <p className="text-muted-foreground mt-2">
                  Please access via a tenant URL (e.g., /pegasos)
                </p>
              </div>
            </div>
          </Route>

          {/* All tenant routes - use nest prop to enable relative routing */}
          <Route path="/:slug" nest>
            <TenantRoutes />
          </Route>
        </Switch>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
